import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAtom } from "jotai";
import { useQuery } from "react-query";
import { getErrorMessage } from "services/api";
import { getItemGroups } from "features/Products/api";
import {
  transformItemsGroup,
  findGroupsAllChildrenIds,
} from "features/Products/helpers";
import { getItemsLeftAmount } from "features/Documents/api";
import useDocuments from "features/Documents/useDocuments";
import { useDocReferenceData } from "features/Documents/useDocReferenceData";
import { openedDocumentAtom } from "atoms/documentsAtom";

import { LoadingView, ErrorView } from "components/Feedback";
import { Accordion } from "components/Accordion";
import { RegularText } from "components/Text";
import { Checkbox } from "components/Checkbox";
import { Button } from "components/Button";
import {
  ModalShell,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useModal,
  InfoModal,
} from "components/Modal";

import styles from "./styles.module.scss";

/**
 * Выбор групп товаров для заполнения инвентаризации по остатку.
 * Выбор родителя автоматически выбирает всех потомков.
 */
export const ItemGroupsSelectModal = () => {
  const { openModal2, closeModal2 } = useModal();
  const { createManyDocItems } = useDocuments();
  const { measureUnits } = useDocReferenceData();
  const [openedDocument] = useAtom(openedDocumentAtom);

  const groupsQuery = useQuery(["item-groups"], () => getItemGroups());
  const groups = groupsQuery.data?.items ?? [];

  const tree = useMemo(() => transformItemsGroup(groups), [groups]);

  const [selectedValues, setSelectedValues] = useState([]);
  const [expanded, setExpanded] = useState(new Set());
  const [isFilling, setIsFilling] = useState(false);
  const [progress, setProgress] = useState(0);
  const [fillError, setFillError] = useState("");
  const progressTimerRef = useRef(null);

  useEffect(
    () => () => {
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    },
    []
  );

  const toggleAccordion = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelect = (groupId) => {
    setSelectedValues((prev) => {
      if (prev.includes(groupId)) return prev.filter((i) => i !== groupId);
      const childrenIds = findGroupsAllChildrenIds(groups, [groupId]);
      return [...new Set([...prev, groupId, ...childrenIds])];
    });
  };

  const handleFill = async () => {
    if (isFilling || !selectedValues.length) return;

    setIsFilling(true);
    setFillError("");
    setProgress(0);

    // Псевдо-прогресс пока ждём ответ (калибровка: 132 группы ≈ 16с)
    const estimatedMs = Math.max(
      1500,
      Math.min(60000, selectedValues.length * (16000 / 132))
    );
    const intervalMs = 120;
    const step = 90 / (estimatedMs / intervalMs);
    progressTimerRef.current = setInterval(() => {
      setProgress((p) => Math.min(90, p + step));
    }, intervalMs);

    try {
      const data = await getItemsLeftAmount({
        itemGroupIds: selectedValues,
        docId: openedDocument.docid,
      });

      setProgress(100);

      if (Array.isArray(data) && data.length) {
        createManyDocItems(data, measureUnits);
        openModal2(
          <InfoModal
            title={`Товары (${data.length}) были успешно добавлены в документ`}
            onClose={closeModal2}
          />
        );
      } else {
        closeModal2();
      }
    } catch (e) {
      setFillError(getErrorMessage(e));
    } finally {
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
      setIsFilling(false);
    }
  };

  const renderItems = (items, depth = 0) =>
    items.map((item) => {
      const checkbox = (
        <div className={styles.checkboxRow} key={item.itemgroupid}>
          <Checkbox
            checked={selectedValues.includes(item.itemgroupid)}
            onChange={() => handleSelect(item.itemgroupid)}
            label={item.name}
          />
        </div>
      );

      if (item.children?.length) {
        return (
          <Accordion
            key={item.itemgroupid}
            withIcon
            arrowZone
            collapsed={!expanded.has(item.itemgroupid)}
            handleCollapse={() => toggleAccordion(item.itemgroupid)}
            parent={checkbox}
          >
            <div style={{ paddingLeft: 20 }}>
              {renderItems(item.children, depth + 1)}
            </div>
          </Accordion>
        );
      }

      return checkbox;
    });

  return (
    <ModalShell width={520} className={styles.modal}>
      <ModalHeader title="Выберите группы товаров" onClose={closeModal2} />
      <ModalBody>
        {isFilling && (
          <div className={styles.progressTrack}>
            <div
              className={styles.progressFill}
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
        {!!fillError && (
          <RegularText color="danger" size={13}>
            {fillError}
          </RegularText>
        )}
        <div className={styles.buttonsRow}>
          <Button
            onClick={() => setSelectedValues(groups.map((g) => g.itemgroupid))}
            disabled={isFilling || selectedValues.length === groups.length}
          >
            Выбрать все
          </Button>
          <Button
            onClick={() => setSelectedValues([])}
            disabled={isFilling || selectedValues.length === 0}
          >
            Снять все
          </Button>
        </div>
        <div className={styles.list}>
          {groupsQuery.isLoading ? (
            <LoadingView />
          ) : groupsQuery.isError ? (
            <ErrorView
              message={getErrorMessage(groupsQuery.error)}
              onRetry={groupsQuery.refetch}
            />
          ) : (
            renderItems(tree)
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="tertiary" onClick={closeModal2} disabled={isFilling}>
          Отмена
        </Button>
        <Button
          variant="secondary"
          disabled={isFilling || !selectedValues.length}
          onClick={handleFill}
          loading={isFilling}
        >
          Выбрать
        </Button>
      </ModalFooter>
    </ModalShell>
  );
};
